import type { MessageDoc } from '@convex-dev/agent';
import { api } from '@packages/backend/convex/_generated/api';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex, usePaginatedQuery } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { AgentOverridesSheetContent } from './components/AgentOverridesPanel';
import { ConversationDetailsPanel } from './components/ConversationDetailsPanel';
import { HumanReplyComposer } from './components/HumanReplyComposer';
import { PendingAdminRequestPanel } from './components/PendingAdminRequestPanel';
import { TranscriptMessage } from './components/TranscriptMessage';

const SHOW_SIMULATE = import.meta.env.VITE_STAGE !== 'production';
const INITIAL_TRANSCRIPT_ITEMS = 50;

export const Route = createFileRoute('/a/conversations/$conversationId')({
  component: Component,
});

function Component() {
  const { conversationId } = Route.useParams();
  const convex = useConvex();
  const bottomRef = useRef<HTMLDivElement>(null);
  const details = useQuery(api.conversations.getConversation, { conversationId });
  const context = useQuery(api.conversations_admin.getConversationContext, {
    conversationId,
  });
  const conversation = details.data?.conversation ?? null;
  const threadId = conversation?.agentThreadId ?? null;
  const pendingAdminRequest = useQuery(
    api.conversations_admin.getPendingAdminRequestForThread,
    threadId ? { threadId } : 'skip',
  );

  const messagesResult = usePaginatedQuery(
    api.conversations_admin.getDetailsMessages,
    threadId !== null ? { conversationId, threadId, includeToolCalls: true } : 'skip',
    { initialNumItems: INITIAL_TRANSCRIPT_ITEMS },
  );
  // `getDetailsMessages` returns `MessageDoc`s but its validator widens to `any`
  // (the agent SDK message validator doesn't generate clean Convex types), so we
  // recover the element type with a plain typed assignment rather than a cast.
  const messageDocs: MessageDoc[] = messagesResult.results;

  const hasUnseenActivity = conversation?.hasUnseenActivity ?? false;
  useEffect(() => {
    if (!hasUnseenActivity) return;
    void convex.mutation(api.conversations_admin.markConversationSeen, { conversationId });
  }, [convex, conversationId, hasUnseenActivity]);

  const transcriptMessages = [...messageDocs].sort(
    (a, b) => a.order - b.order || a.stepOrder - b.stepOrder,
  );

  const contactRoleLabel = conversation?.kind === 'support' ? 'User' : 'Visitor';

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [transcriptMessages.length]);

  if (details.isPending || context.isPending) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <LoadingIcon />
      </div>
    );
  }

  if (details.isError || context.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">
            Error loading conversation. {(details.error ?? context.error)?.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!details.data || !conversation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversation not found</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  const agentInstructions = context.data?.agentInstructions ?? '';
  const restaurantUser = context.data?.restaurantUser ?? null;
  const restaurants = context.data?.restaurants ?? [];
  const customerLabel =
    conversation.kind === 'sales'
      ? `Anonymous ${conversation.visitorIdentifier.slice(0, 8)}`
      : 'Restaurant user';
  const agentTypeLabel = conversation.kind === 'sales' ? 'Sales agent' : 'Support agent';

  return (
    <div className="grid h-full min-h-0 items-stretch gap-4 xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-6">
      <Card className="flex h-full min-h-0 flex-col gap-0 overflow-hidden py-0">
        <CardHeader className="flex flex-row flex-wrap items-center gap-2 px-5 pt-4 pb-4">
          {SHOW_SIMULATE ? (
            <Sheet>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ColorBadge color={conversation.kind === 'sales' ? 'blue' : 'purple'}>
                    {agentTypeLabel}
                  </ColorBadge>
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
                <SheetHeader>
                  <SheetTitle>Agent overrides (dev only)</SheetTitle>
                  <SheetDescription>
                    Reference info for the agent driving this conversation.
                  </SheetDescription>
                </SheetHeader>
                <AgentOverridesSheetContent agentInstructions={agentInstructions} />
              </SheetContent>
            </Sheet>
          ) : (
            <ColorBadge color={conversation.kind === 'sales' ? 'blue' : 'purple'}>
              {agentTypeLabel}
            </ColorBadge>
          )}
        </CardHeader>
        <CardContent className="subtle-scrollbar min-h-0 flex-1 overflow-y-auto border-t px-5 py-5">
          <div className="flex flex-col gap-3">
            {transcriptMessages.map((message) => (
              <TranscriptMessage
                key={message._id}
                message={message}
                contactRoleLabel={contactRoleLabel}
                conversationId={conversationId}
              />
            ))}
            {transcriptMessages.length === 0 && (
              <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
                No messages yet
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </CardContent>
        {pendingAdminRequest.data && (
          <PendingAdminRequestPanel
            adminRequestId={pendingAdminRequest.data._id}
            question={pendingAdminRequest.data.question}
          />
        )}
      </Card>

      <Card className="flex h-full min-h-0 flex-col gap-0 overflow-hidden py-0">
        <CardContent className="flex min-h-0 flex-1 flex-col px-5 py-5">
          <ConversationDetailsPanel
            customerLabel={customerLabel}
            restaurants={restaurants}
            restaurantUser={restaurantUser}
          />
        </CardContent>
        {SHOW_SIMULATE && <HumanReplyComposer conversationId={conversationId} />}
      </Card>
    </div>
  );
}
